import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { AssetSelect, CompanySelect, EditPage, FuseInput, LoanSelect } from '/src/components';
import { useAdjustmentStore, useAssetStore, useFilterStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { Note } from '/src/lib/models';
import { DataModelType, FormErrors, URLString, UpdateAdjustmentForm } from '/src/lib/types';
import { currency, isNumber } from '/src/utils';

/**
 * Edit Adjustment Page
 */
export const EditAdjustmentPage: React.FC = observer(() => {
  /** Context **/
  const adjustmentStore = useAdjustmentStore();
  const filterStore = useFilterStore();
  const assetStore = useAssetStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [formValues, setFormValues] = useState<UpdateAdjustmentForm>();
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /**
   * Array of booleans indicating which sidebar items are open.
   *
   * @remarks
   * Each item's index in the array corresponds to its position in the sidebar.
   */
  const [sidebarItems, setSidebarItems] = useState<boolean[]>([
    false, // Notes
    false, // Files
  ]);

  /** Computed **/
  const assets = useMemo(
    () =>
      assetStore.assets
        ?.filter((asset) => asset.company?._id === formValues?.company)
        .sort((a, b) => a.name.localeCompare(b.name)) ?? [],
    [assetStore.assets, formValues?.company]
  );

  const companies = useMemo(
    () => filterStore.companyListGet().filter((company) => !!company.loans?.length),
    [filterStore._companyList]
  );

  const loans = useMemo(
    () => filterStore.loanListGet().filter((loan) => loan.assets.length && loan.borrower._id === formValues?.company),
    [filterStore._loanList, formValues?.company]
  );

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    if (formValues) {
      setFormValues({
        ...formValues,
        [key]: value,
      });
      // Clear error when field is updated
      if (formErrors[key]) {
        setFormErrors({
          ...formErrors,
          [key]: '',
        });
      }
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formValues?.company) errors.company = 'Company is required';
    if (!formValues?.loan) errors.loan = 'Loan is required';
    if (!formValues?.asset) errors.asset = 'Asset is required';
    if (!formValues?.name) errors.name = 'Name is required';
    if (!isNumber(formValues?.amount)) errors.amount = 'Valid amount is required';
    if (!formValues?.effectiveDate) errors.effectiveDate = 'Date is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Updates the Adjustment.
   *
   * @remarks
   * Redirects to the Adjustment's Detail page afterwards.
   */
  const update = async () => {
    if (!formValues) return;

    if (!validateForm()) {
      toast.error('Please fix the errors first');
      return;
    }

    try {
      const response = await adjustmentStore.update(formValues);
      if (!response) throw new Error(`Could not update Adjustment with ID "${formValues._id}"`);

      toast.success(toastMessages.updateAdjustment.success);
      navigate(`/adjustments/${response._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.updateAdjustment.error);
    }
  };

  /**
   * Updates the Notes list.
   */
  const updateNotes = async (notes: Note[]) => {
    if (!formValues) return;

    await adjustmentStore.update({ ...formValues, notes });

    updateFormValue('notes')(notes);
  };

  /**
   * Updates the Documents list.
   *
   * @param files List of Document URLs.
   */
  const updateFiles = async (files: URLString[]) => {
    if (!formValues) return;

    await adjustmentStore.update({ ...formValues, files });

    updateFormValue('files')(files);
  };

  /** Effects **/

  useEffect(() => {
    if (!params.adjustmentId) return;

    Promise.all([adjustmentStore.get(params.adjustmentId), assetStore.query()]);
  }, [params.adjustmentId]);

  useEffect(() => {
    if (adjustmentStore.currentAdjustment) setFormValues(adjustmentStore.currentAdjustment);
  }, [adjustmentStore.currentAdjustment]);

  /** Render **/
  return (
    <EditPage
      modelName={adjustmentStore.currentAdjustment?.name ?? ''}
      modelType={DataModelType.Adjustment}
      editVerb="Edit"
      onEdit={update}
      sidebarFlex="35"
      contentFlex="65"
      sidebarItems={sidebarItems}
      setSidebarItems={setSidebarItems}
      noteOptions={{
        notes: formValues?.notes ?? [],
        onChange: updateNotes,
      }}
      documentOptions={{
        files: formValues?.files ?? [],
        onChange: updateFiles,
      }}
    >
      {/* Company */}
      <CompanySelect
        value={formValues?.company ?? ''}
        setValue={(companyId) => updateFormValue('company')(companies.find((company) => company._id === companyId))}
        companies={companies}
        disabled
        error={formErrors.company}
      />

      {/* Loan */}
      <LoanSelect
        value={formValues?.loan?._id ?? ''}
        setValue={(loanId) => updateFormValue('loan')(loans.find((loan) => loan._id === loanId))}
        loans={loans}
        disabled
        error={formErrors.loan}
      />

      {/* Asset */}
      <AssetSelect
        value={formValues?.asset?._id ?? ''}
        setValue={(assetId) => updateFormValue('asset')(assets.find((asset) => asset._id === assetId))}
        assets={assets}
        disabled
        error={formErrors.asset}
      />

      <FuseInput
        id="amount"
        label="Amount"
        value={formValues?.amount}
        updateValue={updateFormValue('amount')}
        type="number"
        moneyMask
        required
        error={formErrors.amount}
      />

      {formValues?.asset && typeof formValues.assetValue === 'number' && (
        <Box display="flex">
          <Box
            flex={50}
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            fontSize="1.2em"
            height="100%"
          >
            <Box paddingBottom="6px" marginBottom="6px" borderBottom="solid 1px rgba(0,0,0,0.12)" className="thin">
              Current Asset Value
            </Box>
            <Box id="value">{currency.format(formValues?.assetValue)}</Box>
          </Box>

          <Box
            flex={50}
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            fontSize="1.2em"
            height="100%"
          >
            <Box paddingBottom="6px" marginBottom="6px" borderBottom="solid 1px rgba(0,0,0,0.12)" className="thin">
              Asset Value After Adjustment
            </Box>
            <Box id="projectedValue">
              {currency.format(
                formValues?.amount ? formValues?.assetValue + formValues?.amount : formValues?.assetValue
              )}
            </Box>
          </Box>
        </Box>
      )}

      <FuseInput
        id="name"
        label="Adjustment Name"
        value={formValues?.name}
        updateValue={updateFormValue('name')}
        required
        error={formErrors.name}
      />

      <FuseInput
        id="effectiveDate"
        label="Adjustment Date"
        value={formValues?.effectiveDate}
        updateValue={updateFormValue('effectiveDate')}
        type="date"
        required
        error={formErrors.effectiveDate}
      />
    </EditPage>
  );
});
